<!--
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
-->

<template>
  <PrimeDialog
    :visible="modelValue"
    :modal="true"
    :dismissableMask="false"
    :closeOnEscape="true"
    :closable="true"
    :style="{ width: '90vw', maxWidth: '1200px', height: '85vh' }"
    :contentStyle="{ height: 'calc(100% - 60px)', padding: '1rem' }"
    @update:visible="$emit('update:modelValue', $event)"
  >
    <template #header>
      <div class="dialog-header">
        <i class="ri-emotion-line"></i>
        <span>{{ $t("iconPicker.title") }}</span>
      </div>
    </template>

    <!-- Search Input -->
    <div class="search-wrapper">
      <PrimeInputText
        v-model="searchQuery"
        :placeholder="$t('iconPicker.searchPlaceholder')"
        class="search-input"
      >
        <template #prefix>
          <i class="ri-search-line"></i>
        </template>
      </PrimeInputText>
    </div>

    <!-- Category Tabs -->
    <PrimeTabView v-model:activeIndex="activeTab" class="category-tabs">
      <PrimeTabPanel :header="$t('iconPicker.categories.all')">
        <div class="icons-grid-container">
          <div class="icons-grid">
            <div
              v-for="icon in filteredIcons"
              :key="icon"
              class="icon-item"
              :class="{ selected: selectedIcon === icon }"
              @click="selectIcon(icon)"
              v-tooltip.top="icon"
            >
              <i :class="icon" class="icon-large"></i>
            </div>
          </div>
          <div v-if="filteredIcons.length === 0" class="no-results">
            {{ $t("iconPicker.noResults") }}
          </div>
        </div>
      </PrimeTabPanel>

      <PrimeTabPanel
        v-for="category in categories"
        :key="category.key"
        :header="$t(`iconPicker.categories.${category.key}`)"
      >
        <div class="icons-grid-container">
          <div class="icons-grid">
            <div
              v-for="icon in getIconsByCategory(category.key)"
              :key="icon"
              class="icon-item"
              :class="{ selected: selectedIcon === icon }"
              @click="selectIcon(icon)"
              v-tooltip.top="icon"
            >
              <i :class="icon" class="icon-large"></i>
            </div>
          </div>
        </div>
      </PrimeTabPanel>
    </PrimeTabView>

    <template #footer>
      <PrimeButton
        :label="$t('cancel')"
        text
        @click="$emit('update:modelValue', false)"
      />
      <PrimeButton
        :label="$t('iconPicker.select')"
        @click="confirmSelection"
        :disabled="!selectedIcon"
      />
    </template>
  </PrimeDialog>
</template>

<script>
import Dialog from "primevue/dialog";
import Button from "primevue/button";
import InputText from "primevue/inputtext";
import TabView from "primevue/tabview";
import TabPanel from "primevue/tabpanel";

export default {
  name: "IconPickerDialog",
  components: {
    PrimeDialog: Dialog,
    PrimeButton: Button,
    PrimeInputText: InputText,
    PrimeTabView: TabView,
    PrimeTabPanel: TabPanel,
  },
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    currentIcon: {
      type: String,
      default: "",
    },
  },
  emits: ["update:modelValue", "select"],
  data() {
    return {
      searchQuery: "",
      selectedIcon: this.currentIcon,
      activeTab: 0,
      categories: [
        { key: "system" },
        { key: "communication" },
        { key: "media" },
        { key: "files" },
        { key: "business" },
        { key: "user" },
        { key: "development" },
        { key: "misc" },
      ],
      iconsByCategory: {
        system: [
          "ri-settings-line",
          "ri-settings-3-line",
          "ri-settings-4-line",
          "ri-home-line",
          "ri-home-4-line",
          "ri-dashboard-line",
          "ri-menu-line",
          "ri-apps-line",
          "ri-apps-2-line",
        ],
        communication: [
          "ri-mail-line",
          "ri-mail-send-line",
          "ri-message-line",
          "ri-chat-1-line",
          "ri-chat-3-line",
          "ri-question-answer-line",
          "ri-feedback-line",
          "ri-discuss-line",
          "ri-customer-service-line",
          "ri-phone-line",
          "ri-smartphone-line",
          "ri-contacts-line",
        ],
        media: [
          "ri-video-line",
          "ri-video-chat-line",
          "ri-live-line",
          "ri-webcam-line",
          "ri-camera-line",
          "ri-image-line",
          "ri-gallery-line",
          "ri-film-line",
          "ri-music-line",
          "ri-headphone-line",
          "ri-speaker-line",
          "ri-play-line",
          "ri-pause-line",
        ],
        files: [
          "ri-file-line",
          "ri-file-text-line",
          "ri-file-list-line",
          "ri-file-copy-line",
          "ri-folder-line",
          "ri-folder-open-line",
          "ri-folders-line",
          "ri-file-pdf-line",
          "ri-file-word-line",
          "ri-file-excel-line",
          "ri-file-ppt-line",
          "ri-file-zip-line",
          "ri-file-code-line",
        ],
        business: [
          "ri-calendar-line",
          "ri-calendar-2-line",
          "ri-calendar-event-line",
          "ri-calendar-todo-line",
          "ri-calendar-check-line",
          "ri-time-line",
          "ri-timer-line",
          "ri-alarm-line",
          "ri-task-line",
          "ri-list-check",
          "ri-list-check-2",
          "ri-todo-line",
          "ri-briefcase-line",
          "ri-briefcase-2-line",
          "ri-briefcase-3-line",
          "ri-briefcase-4-line",
          "ri-briefcase-5-line",
          "ri-pie-chart-line",
          "ri-pie-chart-2-line",
          "ri-bar-chart-line",
          "ri-bar-chart-2-line",
          "ri-line-chart-line",
          "ri-donut-chart-line",
          "ri-tools-line",
          "ri-hammer-line",
          "ri-pencil-line",
          "ri-edit-line",
          "ri-edit-2-line",
          "ri-edit-box-line",
          "ri-draft-line",
          "ri-ball-pen-line",
          "ri-quill-pen-line",
          "ri-map-line",
          "ri-map-2-line",
          "ri-map-pin-line",
          "ri-map-pin-2-line",
          "ri-map-pin-3-line",
          "ri-map-pin-4-line",
          "ri-map-pin-5-line",
          "ri-compass-line",
          "ri-compass-2-line",
          "ri-compass-3-line",
          "ri-compass-4-line",
          "ri-navigation-line",
          "ri-road-map-line",
          "ri-route-line",
          "ri-building-line",
          "ri-building-2-line",
          "ri-building-3-line",
          "ri-building-4-line",
          "ri-community-line",
          "ri-government-line",
          "ri-bank-line",
          "ri-store-line",
          "ri-store-2-line",
          "ri-store-3-line",
          "ri-hospital-line",
          "ri-hotel-line",
          "ri-shopping-cart-line",
          "ri-shopping-cart-2-line",
          "ri-shopping-bag-line",
          "ri-shopping-bag-2-line",
          "ri-shopping-bag-3-line",
          "ri-shopping-basket-line",
          "ri-shopping-basket-2-line",
          "ri-price-tag-line",
          "ri-price-tag-2-line",
          "ri-price-tag-3-line",
          "ri-money-dollar-circle-line",
          "ri-money-euro-circle-line",
          "ri-money-pound-circle-line",
          "ri-money-cny-circle-line",
          "ri-coin-line",
          "ri-coins-line",
          "ri-currency-line",
          "ri-wallet-line",
          "ri-wallet-2-line",
          "ri-wallet-3-line",
          "ri-bank-card-line",
          "ri-bank-card-2-line",
        ],
        user: [
          "ri-user-line",
          "ri-user-2-line",
          "ri-user-3-line",
          "ri-user-4-line",
          "ri-user-5-line",
          "ri-user-6-line",
          "ri-team-line",
          "ri-group-line",
          "ri-account-circle-line",
          "ri-account-box-line",
          "ri-emotion-line",
          "ri-emotion-happy-line",
          "ri-emotion-normal-line",
          "ri-emotion-unhappy-line",
        ],
        development: [
          "ri-code-line",
          "ri-code-s-line",
          "ri-code-box-line",
          "ri-terminal-line",
          "ri-terminal-box-line",
          "ri-bug-line",
          "ri-bug-2-line",
          "ri-command-line",
          "ri-function-line",
        ],
        misc: [
          "ri-checkbox-line",
          "ri-checkbox-circle-line",
          "ri-palette-line",
          "ri-brush-line",
          "ri-brush-2-line",
          "ri-brush-3-line",
          "ri-brush-4-line",
          "ri-paint-brush-line",
          "ri-pantone-line",
          "ri-contrast-line",
          "ri-contrast-2-line",
          "ri-test-tube-line",
          "ri-microscope-line",
          "ri-flask-line",
          "ri-sun-line",
          "ri-moon-line",
          "ri-cloudy-line",
          "ri-cloudy-2-line",
          "ri-rainy-line",
          "ri-snowy-line",
          "ri-thunderstorms-line",
          "ri-car-line",
          "ri-bus-line",
          "ri-train-line",
          "ri-subway-line",
          "ri-plane-line",
          "ri-ship-line",
          "ri-bike-line",
          "ri-e-bike-line",
          "ri-motorbike-line",
          "ri-taxi-line",
          "ri-truck-line",
          "ri-lock-line",
          "ri-lock-2-line",
          "ri-lock-unlock-line",
          "ri-key-line",
          "ri-key-2-line",
          "ri-shield-line",
          "ri-shield-check-line",
          "ri-shield-cross-line",
          "ri-shield-star-line",
          "ri-shield-user-line",
          "ri-heart-line",
          "ri-heart-2-line",
          "ri-heart-3-line",
          "ri-heart-pulse-line",
          "ri-health-book-line",
          "ri-mental-health-line",
          "ri-empathize-line",
          "ri-stethoscope-line",
          "ri-syringe-line",
          "ri-capsule-line",
          "ri-medicine-bottle-line",
          "ri-leaf-line",
          "ri-plant-line",
          "ri-seedling-line",
          "ri-tree-line",
          "ri-flower-line",
          "ri-book-line",
          "ri-book-2-line",
          "ri-book-3-line",
          "ri-book-open-line",
          "ri-bookmark-line",
          "ri-bookmark-2-line",
          "ri-bookmark-3-line",
          "ri-lightbulb-line",
          "ri-lightbulb-flash-line",
          "ri-flashlight-line",
          "ri-trophy-line",
          "ri-award-line",
          "ri-medal-line",
          "ri-medal-2-line",
          "ri-vip-crown-line",
          "ri-vip-crown-2-line",
          "ri-fire-line",
          "ri-contrast-drop-line",
          "ri-contrast-drop-2-line",
          "ri-drop-line",
          "ri-rocket-line",
          "ri-rocket-2-line",
          "ri-space-ship-line",
          "ri-earth-line",
          "ri-global-line",
          "ri-planet-line",
          "ri-star-line",
          "ri-star-s-line",
          "ri-magic-line",
          "ri-focus-2-line",
          "ri-focus-3-line",
          "ri-links-line",
          "ri-link",
          "ri-link-unlink",
          "ri-link-m",
          "ri-anchor-line",
          "ri-question-line",
          "ri-question-mark",
          "ri-information-line",
          "ri-alert-line",
          "ri-error-warning-line",
          "ri-close-circle-line",
          "ri-add-circle-line",
          "ri-indeterminate-circle-line",
        ],
      },
    };
  },
  computed: {
    filteredIcons() {
      const allIcons = Object.values(this.iconsByCategory).flat();
      if (!this.searchQuery) {
        return allIcons;
      }
      const query = this.searchQuery.toLowerCase();
      return allIcons.filter((icon) => icon.toLowerCase().includes(query));
    },
  },
  watch: {
    currentIcon(newValue) {
      this.selectedIcon = newValue;
    },
    modelValue(newValue) {
      if (newValue) {
        this.selectedIcon = this.currentIcon;
        this.searchQuery = "";
      }
    },
  },
  methods: {
    selectIcon(icon) {
      this.selectedIcon = icon;
    },
    confirmSelection() {
      if (this.selectedIcon) {
        this.$emit("select", this.selectedIcon);
        this.$emit("update:modelValue", false);
      }
    },
    getIconsByCategory(categoryKey) {
      const icons = this.iconsByCategory[categoryKey] || [];
      if (!this.searchQuery) {
        return icons;
      }
      const query = this.searchQuery.toLowerCase();
      return icons.filter((icon) => icon.toLowerCase().includes(query));
    },
  },
};
</script>

<style scoped>
.dialog-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.search-wrapper {
  margin-bottom: 0.75rem;
}

.search-input {
  width: 100%;
}

.category-tabs {
  margin-bottom: 0.75rem;
}

.icon-large {
  font-size: 1.5rem;
}

.no-results {
  text-align: center;
  padding: 1rem;
  color: var(--text-color-secondary);
}

.icons-grid-container {
  overflow-y: auto;
  max-height: calc(80vh - 200px);
  border: 1px solid var(--surface-border);
  border-radius: var(--border-radius);
  padding: 0.5rem;
}

.icons-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(60px, 1fr));
  gap: 0.5rem;
}

.icon-item {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 60px;
  border: 2px solid var(--surface-border);
  border-radius: var(--border-radius);
  cursor: pointer;
  transition: all 0.2s;
  background: var(--surface-card);
}

.icon-item:hover {
  background: var(--surface-hover);
  border-color: var(--primary-color);
}

.icon-item.selected {
  background: var(--primary-color);
  color: var(--primary-color-text);
  border-color: var(--primary-color);
}

:deep(.p-inputtext) {
  padding-left: 2.5rem;
}

:deep(.p-inputtext-prefix) {
  position: absolute;
  left: 0.75rem;
  color: var(--text-color-secondary);
}
</style>
